import Client from "./client";

/**
 * Adds an image to a project's library (see api/lib/imageLibrary.js).
 *
 * Resolves with the stored image, not just a URL: the picker that called
 * this wants to show it in the grid straight away, and its `url` is the
 * friendly one to write into a document.
 *
 * `projectId` is required now rather than a hint - an image belongs to the
 * project it will be listed under and counted against.
 */
export const UploadDocumentImage = ({file, projectId, description, accessToken}) => {
    return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("projectId", projectId);
        if (description) formData.append("description", description);

        Client.post('/api/images', formData, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.image);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong uploading the image. Please try again." });
        })
    })
}

/**
 * A page of the project's images, newest first.
 *
 * `search` matches filename and description together. `cursor` continues a
 * previous page - the picker asks for the next one as the grid is scrolled
 * rather than loading a project's whole library up front.
 */
export const ListProjectImages = ({projectId, search, cursor, limit, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/images`, {
            params: {
                ...(search ? {search} : {}),
                ...(cursor ? {cursor} : {}),
                ...(limit ? {limit} : {}),
            },
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong loading images. Please try again." });
        })
    })
}


// The plan's image rules, so the browser can shrink a file to fit before
// sending it (see lib/resizeImage.js). Advisory: the API applies the same
// rules again to whatever actually arrives.
export const GetImagePolicy = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get('/api/images/policy', {
            params: projectId ? { projectId } : {},
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.policy);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

/**
 * Removes an image from the project's library - the object, its preview and
 * its record.
 *
 * Refused with a 409 carrying `total` and `references` if anything still
 * uses it: an image lives in documents by URL, not by foreign key, so
 * nothing in the database would otherwise stop a delete that leaves a
 * broken picture in somebody's ticket. `force` is the caller saying they
 * want that anyway, which is a real thing to want.
 */
export const DeleteProjectImage = ({imageId, force, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete(`/api/images/${imageId}`, {
            params: force ? {force: true} : {},
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong removing the image. Please try again." });
        })
    })
}

// What would break if this image went. Read before offering to delete one,
// so the confirmation can name what it is about to affect.
export const GetImageUsage = ({imageId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/images/${imageId}/usage`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

/**
 * What the project's owner is storing, against what their plan allows.
 *
 * `used` and `limit` span every project that account owns - the allowance
 * is per account (see api/lib/limits.js) - while `images` is this project's
 * own count, which is the part somebody on this project can act on.
 * `limit` is null for a plan that isn't capped.
 */
export const GetProjectStorage = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/storage`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong loading storage." });
        })
    })
}
